<template>
  <div class="min-h-screen bg-gray-50" style="font-family: 'Inter', system-ui, -apple-system, sans-serif;">
    <!-- Header -->
    <div class="bg-white shadow-sm p-2 sticky top-0 z-10">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <router-link
            to="/dashboard"
            class="p-1 text-gray-600 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </router-link>
          <h1 class="text-lg font-semibold text-gray-900">Label Printing</h1>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-6xl mx-auto p-4 space-y-6 pb-32 lg:pb-8">
      <!-- Search and Scanner Section -->
      <div class="bg-white rounded-lg shadow-sm p-4">
        <div class="flex space-x-3">
          <!-- Search Input -->
          <div class="flex-1 relative">
            <input
              v-model="searchQuery"
              @input="searchProducts"
              type="text"
              placeholder="Search products by name, barcode, or short code..."
              class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
            
            <!-- Search Results Dropdown -->
            <div v-if="searchResults.length > 0 && searchQuery" class="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-30 max-h-60 overflow-y-auto">
              <div 
                v-for="product in searchResults" 
                :key="product._id"
                @click="addProductToList(product)"
                class="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div class="flex justify-between items-center">
                  <div>
                    <div class="font-medium text-gray-900">{{ product.name }}</div>
                    <div class="text-sm text-gray-500">
                      {{ product.short_code }} | {{ product.barcode }} | Stock: {{ product.current_stock }}
                    </div>
                  </div>
                  <div class="text-lg font-semibold text-green-600">₹{{ product.pricing?.sale_price_inc_tax || 'N/A' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Barcode Scanner Button -->
          <button
            @click="toggleBarcodeScanner"
            class="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
            title="Barcode Scanner"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            <span class="hidden sm:inline">Scan</span>
          </button>
        </div>
      </div>

      <!-- Template Preview -->
      <div class="bg-white rounded-lg shadow-sm p-4">
        <div class="flex justify-between items-center mb-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Label Template</h3>
          </div>
          <div class="flex items-center space-x-3">
            <!-- Template Selection -->
            <select 
              v-model="selectedTemplate" 
              class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :disabled="!hasTemplates"
            >
              <option v-if="!hasTemplates" value="">No templates available</option>
              <option v-for="template in stickerTemplates" :key="template.id" :value="template">
                {{ template.name }} ({{ template.size }})
              </option>
            </select>
            <!-- Template Management Button -->
            <router-link
              to="/template-management"
              class="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
              title="Manage Templates"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/>
              </svg>
              <span class="hidden sm:inline">Manage</span>
            </router-link>
          </div>
        </div>
        <div class="flex justify-center">
          <!-- No Templates Message -->
          <div v-if="!hasTemplates" class="text-center py-8">
            <div class="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
              <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <h3 class="text-lg font-medium text-gray-900 mb-2">No Templates Available</h3>
              <p class="text-gray-500 mb-4">Create your first label template to get started with printing labels.</p>
              <router-link
                to="/template-management"
                class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                Create Template
              </router-link>
            </div>
          </div>
          <!-- Template Preview -->
          <div v-else-if="selectedTemplate && selectedTemplate.isCustom && selectedTemplate.fields" 
               :style="actualPreviewStyle" 
               class="relative bg-white">
            <!-- Content Area Guide -->
            <div 
              class="absolute inset-1 border border-dashed border-blue-200 pointer-events-none z-0"
              :style="{
                left: '2px',
                top: '2px',
                right: '2px',
                bottom: '2px'
              }"
            ></div>
            
            <!-- Custom Template Preview -->
            <div 
              v-for="(field, index) in selectedTemplate.fields" 
              :key="field.id"
              :style="{
                position: 'absolute',
                left: field.x || '5px',
                top: field.y || (10 + index * 20) + 'px',
                fontSize: field.fontSize || '12px',
                fontWeight: field.fontWeight || 'normal',
                textAlign: field.textAlign || 'left',
                color: field.color || '#000000',
                textDecoration: field.textDecoration || 'none',
                fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
              }"
              class="absolute text-xs"
            >
              <span v-if="field.type === 'product_barcode_image'" class="bg-gray-100 px-1 rounded text-xs">[BARCODE]</span>
              <span v-else-if="field.type === 'horizontal_line'">────────</span>
              <span v-else-if="field.type === 'vertical_line'" class="inline-block" style="writing-mode: vertical-lr;">│</span>
              <span v-else>{{ getPreviewFieldValue(field) }}</span>
            </div>
          </div>
          
          <!-- No Template Selected Message -->
          <div v-else class="text-center py-8">
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <svg class="mx-auto h-12 w-12 text-yellow-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Select a Template</h3>
              <p class="text-gray-600">Please select a template from the dropdown above to preview your labels.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Products List -->
      <div class="bg-white rounded-lg shadow-sm">
        <div class="p-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">Products to Print</h3>
        </div>
        
        <div v-if="labelProducts.length === 0" class="p-8 text-center text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a2 2 0 012-2z"/>
          </svg>
          <p>No products selected for printing</p>
          <p class="text-sm">Search or scan products to add them</p>
        </div>

        <div v-else class="divide-y divide-gray-200">
          <div 
            v-for="(product, index) in labelProducts" 
            :key="`${product._id}-${index}`"
            class="p-3"
          >
            <div class="flex items-center justify-between">
              <!-- Left side: Product info -->
              <div class="flex-1">
                <!-- Product name with inline quantity input -->
                <div class="flex items-center space-x-3 mb-1">
                  <h4 class="font-medium text-gray-900 text-sm flex-1">{{ product.name }}</h4>
                  <div class="flex items-center space-x-2">
                    <label class="text-xs text-gray-500">Qty:</label>
                    <input
                      v-model.number="product.labelQuantity"
                      type="number"
                      min="1"
                      max="1000"
                      class="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="1"
                    >
                  </div>
                </div>
                <!-- Barcode -->
                <p class="text-xs text-gray-500 mb-1">{{ product.barcode || 'No Barcode' }}</p>
                <!-- Prices under barcode -->
                <div class="flex items-center space-x-4">
                  <span class="text-xs text-green-600 font-medium">Sell: ₹{{ product.pricing?.sale_price_inc_tax || 'N/A' }}</span>
                  <span class="text-xs text-gray-500">MRP: ₹{{ product.pricing?.mrp || 'N/A' }}</span>
                </div>
              </div>

              <!-- Right side: Remove button -->
              <div class="ml-3">
                <button
                  @click="removeProduct(index)"
                  class="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                  title="Remove Product"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Print Settings -->
      <div class="bg-white rounded-lg shadow-sm p-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Print Settings</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Printer Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Printer Type</label>
            <select 
              v-model="printSettings.type" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="browser">Browser Print</option>
              <option value="thermal">Thermal Printer</option>
            </select>
          </div>

          <!-- Labels per Row -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Labels per Row</label>
            <select 
              v-model.number="printSettings.labelsPerRow" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option :value="1">1</option>
              <option :value="2">2</option>
              <option :value="3">3</option>
              <option :value="4">4</option>
            </select>
          </div>

          <!-- Thermal Print Mode (show only if thermal is selected) -->
          <div v-if="printSettings.type === 'thermal'" class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-2">Thermal Print Mode</label>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label class="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" 
                     :class="{ 'border-blue-500 bg-blue-50': printSettings.thermalMode === 'text' }">
                <input 
                  type="radio" 
                  v-model="printSettings.thermalMode" 
                  value="text" 
                  class="sr-only"
                >
                <div class="flex items-center space-x-3 w-full">
                  <div class="flex-shrink-0">
                    <div class="w-4 h-4 border-2 rounded-full flex items-center justify-center"
                         :class="{ 'border-blue-500 bg-blue-500': printSettings.thermalMode === 'text', 'border-gray-300': printSettings.thermalMode !== 'text' }">
                      <div v-if="printSettings.thermalMode === 'text'" class="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-medium">Text Mode (Fast)</span>
                      <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414-1.414L9 5.586 7.707 4.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L10 4.414z" clip-rule="evenodd"/>
                      </svg>
                    </div>
                    <p class="text-xs text-gray-600 mt-1">Uses ESC/POS commands. Fast printing but basic formatting.</p>
                  </div>
                </div>
              </label>
              
              <label class="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                     :class="{ 'border-blue-500 bg-blue-50': printSettings.thermalMode === 'image' }">
                <input 
                  type="radio" 
                  v-model="printSettings.thermalMode" 
                  value="image" 
                  class="sr-only"
                >
                <div class="flex items-center space-x-3 w-full">
                  <div class="flex-shrink-0">
                    <div class="w-4 h-4 border-2 rounded-full flex items-center justify-center"
                         :class="{ 'border-blue-500 bg-blue-500': printSettings.thermalMode === 'image', 'border-gray-300': printSettings.thermalMode !== 'image' }">
                      <div v-if="printSettings.thermalMode === 'image'" class="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <span class="text-sm font-medium">Image Mode (Accurate)</span>
                      <svg class="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                      </svg>
                    </div>
                    <p class="text-xs text-gray-600 mt-1">Renders as image. Pixel-perfect but slower and larger data.</p>
                  </div>
                </div>
              </label>
            </div>
            
            <!-- GOOJPRT PT-265 Optimization Notice -->
            <div class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <p class="text-xs text-blue-800 font-medium">GOOJPRT PT-265 Optimized</p>
              </div>
              <p class="text-xs text-blue-700 mt-1">
                Optimized for 58mm paper with maximum heat density, 512-byte chunks, and 5ms delays for fastest printing.
              </p>
            </div>
          </div>
          
          <!-- Thermal Paper Settings -->
          <div class="mt-4 grid grid-cols-2 gap-3">
            <!-- Paper Width -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Paper Width</label>
              <select 
                v-model.number="printSettings.thermalPaperWidth"
                class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option :value="58">58mm (GOOJPRT PT-265)</option>
                <option :value="38">38mm</option>
                <option :value="48">48mm</option>
                <option :value="80">80mm</option>
              </select>
            </div>
            
            <!-- Margins -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Margins</label>
              <select 
                v-model.number="printSettings.thermalMargins"
                class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option :value="1">1mm</option>
                <option :value="2">2mm</option>
                <option :value="3">3mm</option>
                <option :value="4">4mm</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Thermal Printer Status (show only if thermal is selected) -->
        <div v-if="printSettings.type === 'thermal'" class="mt-4 p-3 bg-gray-50 rounded-lg border">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H11a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clip-rule="evenodd"/>
                </svg>
                <span class="text-sm font-medium text-gray-700">Thermal Printer</span>
              </div>
              
              <!-- Printer Status -->
              <div class="flex items-center space-x-2">
                <div 
                  :class="{
                    'bg-green-100 text-green-800': bluetoothStatus === 'connected',
                    'bg-yellow-100 text-yellow-800': bluetoothStatus === 'connecting',
                    'bg-red-100 text-red-800': bluetoothStatus === 'disconnected'
                  }"
                  class="px-2 py-1 rounded-full text-xs font-medium"
                >
                  <div class="flex items-center space-x-1">
                    <div 
                      v-if="bluetoothStatus === 'connecting'"
                      class="w-2 h-2 bg-yellow-600 rounded-full animate-pulse"
                    ></div>
                    <div 
                      v-else-if="bluetoothStatus === 'connected'"
                      class="w-2 h-2 bg-green-600 rounded-full"
                    ></div>
                    <div 
                      v-else
                      class="w-2 h-2 bg-red-600 rounded-full"
                    ></div>
                    <span>
                      {{ bluetoothStatus === 'connected' ? 'Connected' : 
                          bluetoothStatus === 'connecting' ? 'Connecting...' : 'Disconnected' }}
                    </span>
                  </div>
                </div>
                
                <!-- Printer Name -->
                <span v-if="connectedPrinter" class="text-sm text-gray-600">
                  ({{ connectedPrinter.name }})
                </span>
              </div>
            </div>
            
            <!-- Printer Actions -->
            <div class="flex items-center space-x-2">
              <!-- Settings Button -->
              <button
                @click="$router.push('/settings')"
                class="px-2 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 text-sm"
                title="Go to Settings"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/>
                </svg>
              </button>
              
              <!-- Connect/Scan Button -->
              <button
                v-if="bluetoothStatus !== 'connected'"
                @click="scanForPrinters"
                :disabled="bluetoothStatus === 'connecting' || isScanning"
                class="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
              >
                <div class="flex items-center space-x-1">
                  <svg v-if="isScanning" class="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0011.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clip-rule="evenodd"/>
                  </svg>
                  <span>{{ isScanning ? 'Scanning...' : 'Scan' }}</span>
                </div>
              </button>
              
              <!-- Disconnect Button -->
              <button
                v-if="bluetoothStatus === 'connected'"
                @click="disconnectCurrentPrinter"
                class="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm font-medium"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Print Button Section -->
      <div v-if="labelProducts.length > 0" class="bg-white rounded-lg shadow-sm p-4">
        <button
          @click="printLabels"
          :disabled="totalLabels === 0 || !selectedTemplate || !hasTemplates"
          class="w-full bg-green-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2 2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
          </svg>
          <span v-if="!hasTemplates">Create Template to Print</span>
          <span v-else-if="!selectedTemplate">Select Template to Print</span>
          <span v-else>Print {{ totalLabels }} Labels</span>
        </button>
      </div>
    </div>

    <!-- Barcode Scanner Modal -->
    <div v-if="showScannerModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="stopBarcodeScanner">
      <div class="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-semibold">Scan Product Barcode</h3>
          <button @click="stopBarcodeScanner" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-4">
          <div id="label-barcode-scanner" class="w-full"></div>
          <p class="text-sm text-gray-600 mt-4 text-center">Position barcode within the frame</p>
        </div>
      </div>
    </div>

    <!-- Print Preview Modal -->
    <div v-if="showPrintPreview" class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50 flex items-center justify-center p-4">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showPrintPreview = false"></div>
      
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-10">
        <div class="flex justify-between items-center p-6 border-b">
          <h3 class="text-lg font-semibold">Print Preview</h3>
          <div class="flex space-x-2">
            <button
              @click="actuallyPrint"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Print Now
            </button>
            <button
              @click="showPrintPreview = false"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="p-6">
          <div id="print-preview-content" class="space-y-4">
            <!-- Print content will be generated here -->
            <div class="text-center py-8 text-gray-500">
              Preparing labels for preview...
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Thermal Print Preview Modal -->
    <div v-if="showThermalPreview" class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50 flex items-center justify-center p-1">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showThermalPreview = false"></div>
      
      <div class="bg-white rounded-lg max-w-sm w-full max-h-[95vh] overflow-y-auto relative z-10 m-1">
        <div class="flex justify-between items-center p-3 border-b">
          <div class="flex-1 min-w-0">
            <h3 class="text-base font-semibold truncate">Thermal Preview</h3>
            <p class="text-xs text-gray-600 truncate">
              {{ printSettings.thermalMode === 'image' ? 'Image Mode' : 'Text Mode' }} • {{ totalLabels }} labels
            </p>
          </div>
          <div class="flex space-x-1 ml-2">
            <button
              @click="proceedWithThermalPrint"
              class="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center space-x-1"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V9a2 2 0 00-2-2H9a2 2 0 00-2 2v.01"/>
              </svg>
              <span>Print</span>
            </button>
            <button
              @click="showThermalPreview = false"
              class="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="p-3">
          <!-- Preview Settings -->
          <div class="mb-3 p-2 bg-gray-50 rounded text-xs">
            <div class="flex items-center justify-between">
              <span class="text-gray-600">Mode:</span>
              <span class="font-medium" :class="{
                'text-green-600': printSettings.thermalMode === 'text',
                'text-blue-600': printSettings.thermalMode === 'image'
              }">
                {{ printSettings.thermalMode === 'image' ? 'Image' : 'Text' }}
              </span>
            </div>
          </div>
          
          <!-- Preview Content -->
          <div class="space-y-2 max-h-[70vh] overflow-y-auto">
            <div v-if="thermalPreviewLoading" class="text-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-1"></div>
              <p class="text-gray-600 text-xs">Generating...</p>
            </div>
            
            <div v-else class="grid gap-2">
              <div 
                v-for="(preview, index) in thermalPreviews" 
                :key="index"
                class="border rounded p-2 bg-white"
              >
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs font-medium text-gray-700">{{ index + 1 }}</span>
                  <span class="text-xs text-gray-500 truncate ml-2">{{ preview.product?.name || 'Product' }}</span>
                </div>
                
                <!-- Canvas Preview for Image Mode -->
                <div v-if="printSettings.thermalMode === 'image'" class="border bg-white p-1 rounded">
                  <canvas 
                    :ref="el => { if (el) preview.canvasEl = el }"
                    class="max-w-full h-auto border"
                    style="image-rendering: pixelated; background: white;"
                  ></canvas>
                </div>
                
                <!-- Text Preview for Text Mode -->
                <div v-else class="border bg-black text-white p-2 rounded font-mono text-xs leading-tight" style="font-family: 'Courier New', monospace;">
                  <pre class="whitespace-pre-wrap">{{ preview.textPreview }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Navigation -->
    <BottomNavigation />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { productsApi } from '@/services/products'
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats, Html5QrcodeScanType } from 'html5-qrcode'
import JsBarcode from 'jsbarcode'
import BottomNavigation from '@/components/BottomNavigation.vue'

const toast = useToast()
const authStore = useAuthStore()

// Reactive data
const searchQuery = ref('')
const searchResults = ref([])
const labelProducts = ref([])
const allProducts = ref([])
const showScannerModal = ref(false)
const showPrintPreview = ref(false)
const showThermalPreview = ref(false)
const thermalPreviewLoading = ref(false)
const thermalPreviews = ref([])
const scanner = ref(null)

// Thermal printer state
const bluetoothStatus = ref('disconnected') // 'connected', 'connecting', 'disconnected'
const isScanning = ref(false)
const connectedPrinter = ref(null)
const availablePrinters = ref([])

// Sticker Templates - Only custom templates will be loaded
const stickerTemplates = ref([])

// Load saved templates from localStorage
const loadSavedTemplates = () => {
  try {
    const savedTemplates = JSON.parse(localStorage.getItem('labelTemplates') || '[]')
    if (savedTemplates.length > 0) {
      // Process saved templates to ensure they have all required properties
      const processedTemplates = savedTemplates.map(template => ({
        id: template.id,
        name: template.name,
        size: template.size || `${template.width}×${template.height}`,
        layout: template.layout || 'Custom Layout',
        showPrice: template.showPrice !== undefined ? template.showPrice : true,
        previewClass: template.previewClass || generatePreviewClass(template),
        printClass: template.printClass || generatePrintClass(template),
        width: template.width,
        height: template.height,
        isCustom: true,
        fields: template.fields || []
      }))
      
      // Add processed templates to the existing ones
      stickerTemplates.value.push(...processedTemplates)
      
      // Set template based on saved selection or first available
      if (!selectedTemplate.value) {
        const savedTemplateId = printSettings.value.selectedTemplateId
        let templateToSelect = null
        
        if (savedTemplateId) {
          // Try to find the previously selected template
          templateToSelect = [...stickerTemplates.value, ...processedTemplates].find(t => t.id === savedTemplateId)
        }
        
        // Fallback to first template if saved template not found
        if (!templateToSelect && processedTemplates.length > 0) {
          templateToSelect = processedTemplates[0]
        } else if (!templateToSelect && stickerTemplates.value.length > 0) {
          templateToSelect = stickerTemplates.value[0]
        }
        
        if (templateToSelect) {
          selectedTemplate.value = templateToSelect
        }
      }
    }
  } catch (error) {
    console.error('Error loading saved templates:', error)
  }
}

// Helper functions to generate CSS classes for saved templates
const generatePreviewClass = (template) => {
  const width = parseInt(template.width) || 25
  const height = parseInt(template.height) || 25
  
  if (width <= 25 && height <= 25) return 'w-20 h-20'
  if (width <= 30 && height <= 30) return 'w-24 h-24'
  if (width <= 40 && height <= 30) return 'w-28 h-20'
  if (width <= 50 && height <= 30) return 'w-32 h-20'
  if (width <= 60 && height <= 35) return 'w-40 h-24'
  return 'w-48 h-28'
}

const generatePrintClass = (template) => {
  const width = parseInt(template.width) || 25
  const height = parseInt(template.height) || 25
  return `label-${width}x${height}`
}

const selectedTemplate = ref(null) // Will be set after templates are loaded

// Print Settings with localStorage persistence
const defaultPrintSettings = {
  type: 'browser',
  labelsPerRow: 2,
  thermalMode: 'text', // 'text' or 'image'
  thermalPaperWidth: 58, // GOOJPRT PT-265: 58mm thermal paper
  thermalMargins: 2, // 2mm margins
  selectedTemplateId: null
}

const loadPrintSettings = () => {
  try {
    const saved = localStorage.getItem('quickbill-print-settings')
    if (saved) {
      const parsed = JSON.parse(saved)
      return { ...defaultPrintSettings, ...parsed }
    }
  } catch (error) {
    console.warn('Failed to load print settings:', error)
  }
  return defaultPrintSettings
}

const savePrintSettings = (settings) => {
  try {
    localStorage.setItem('quickbill-print-settings', JSON.stringify(settings))
  } catch (error) {
    console.warn('Failed to save print settings:', error)
  }
}

const printSettings = ref(loadPrintSettings())

// Load templates after printSettings is initialized
loadSavedTemplates()

// Save settings whenever they change
watch(printSettings, (newSettings) => {
  savePrintSettings(newSettings)
}, { deep: true })

// Save selected template when it changes
watch(selectedTemplate, (newTemplate) => {
  if (newTemplate && newTemplate.id) {
    printSettings.value.selectedTemplateId = newTemplate.id
  }
}, { immediate: false })

// Computed properties
const hasTemplates = computed(() => {
  return stickerTemplates.value.length > 0
})

const totalLabels = computed(() => {
  return labelProducts.value.reduce((total, product) => {
    return total + (product.labelQuantity || 0)
  }, 0)
})

const actualPreviewStyle = computed(() => {
  if (!selectedTemplate.value) return {}
  
  const width = parseInt(selectedTemplate.value.width) || 50
  const height = parseInt(selectedTemplate.value.height) || 25
  
  // Use consistent pixel scaling to maintain accurate proportions
  const scale = 4 // 4 pixels per mm for consistent proportions
  const widthPx = width * scale
  const heightPx = height * scale
  
  return {
    width: `${widthPx}px`,
    height: `${heightPx}px`,
    border: '2px solid #374151',
    borderRadius: '2px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    overflow: 'hidden', // Prevent content from overflowing
    position: 'relative'
  }
})

// Methods
const searchProducts = async () => {
  if (searchQuery.value.length < 2) {
    searchResults.value = []
    return
  }

  try {
    const businessId = authStore.businessId
    if (!businessId) {
      toast.error('Business context not available')
      return
    }

    const response = await productsApi.getProducts({
      business_id: businessId,
      search: searchQuery.value,
      limit: 10
    })

    searchResults.value = response.data || response.products || []
  } catch (error) {
    console.error('Error searching products:', error)
    toast.error('Error searching products')
  }
}

const addProductToList = (product) => {
  // Check if product already exists
  const existingIndex = labelProducts.value.findIndex(p => p._id === product._id)
  
  if (existingIndex > -1) {
    // Increment quantity if already exists
    labelProducts.value[existingIndex].labelQuantity += 1
    toast.success(`Increased quantity for ${product.name}`)
  } else {
    // Add new product with default quantity of 1
    labelProducts.value.push({
      ...product,
      labelQuantity: 1
    })
    toast.success(`Added ${product.name} to print list`)
  }

  // Clear search
  searchQuery.value = ''
  searchResults.value = []
}

const removeProduct = (index) => {
  const product = labelProducts.value[index]
  labelProducts.value.splice(index, 1)
  toast.success(`Removed ${product.name} from print list`)
}

// Barcode Scanner Functions
const toggleBarcodeScanner = () => {
  if (showScannerModal.value) {
    stopBarcodeScanner()
  } else {
    startBarcodeScanner()
  }
}

const startBarcodeScanner = () => {
  console.log('Starting label barcode scanner...')
  showScannerModal.value = true

  nextTick(() => {
    scanner.value = new Html5QrcodeScanner(
      "label-barcode-scanner",
      { 
        fps: 20,
        qrbox: { width: 250, height: 150 },
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39
        ],
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
        showTorchButtonIfSupported: true,
        showZoomSliderIfSupported: true,
        rememberLastUsedCamera: true,
        aspectRatio: 1.5,
        defaultZoomValueIfSupported: 2
      },
      false
    )
    
    try {
      console.log('Starting label barcode scanner...')
      scanner.value.render(onScanSuccess, onScanFailure)
      console.log('Label barcode scanner rendered successfully')
      toast.success('Camera started. Hold barcode steady in the scanning area.')
    } catch (err) {
      console.error('Error rendering label barcode scanner:', err)
      toast.error('Failed to start camera for barcode scanning')
    }
  })
}

const stopBarcodeScanner = () => {
  if (scanner.value) {
    scanner.value.clear().catch(console.warn)
    scanner.value = null
  }
  showScannerModal.value = false
}

const onScanSuccess = async (decodedText) => {
  try {
    console.log('Barcode scanned in Label Printing:', decodedText)
    
    // Stop scanner first
    stopBarcodeScanner()
    
    // Search for product by barcode in allProducts or search via API
    let foundProduct = allProducts.value.find(p => 
      p.barcode === decodedText || 
      p.short_code === decodedText
    )
    
    // If not found in loaded products, try searching via API
    if (!foundProduct) {
      try {
        const businessId = authStore.businessId
        if (!businessId) {
          toast.error('Business context not available')
          return
        }

        const response = await productsApi.getProducts({
          business_id: businessId,
          search: decodedText,
          limit: 10
        })
        
        if (response.products && response.products.length > 0) {
          // Find exact match by barcode or short_code
          foundProduct = response.products.find(p => 
            p.barcode === decodedText || 
            p.short_code === decodedText
          )
          
          // If no exact match, use the first result
          if (!foundProduct) {
            foundProduct = response.products[0]
          }
        }
      } catch (apiError) {
        console.error('Error searching product:', apiError)
      }
    }
    
    if (foundProduct) {
      // Auto-add the scanned product to label list
      addProductToList(foundProduct)
    } else {
      toast.error(`Product not found for barcode: ${decodedText}`)
    }
  } catch (error) {
    console.error('Barcode scan error:', error)
    toast.error('Error processing barcode scan')
  }
}

const onScanFailure = (error) => {
  // Only log significant errors, not routine scan attempts
  if (error && 
      !error.includes('No QR code found') && 
      !error.includes('NotFoundException') &&
      !error.includes('No MultiFormat Readers') &&
      !error.includes('NotFoundError')) {
    console.log('Label barcode scan attempt:', error)
  }
}

// Print Functions
const printLabels = async () => {
  if (!selectedTemplate.value) {
    toast.error('Please select a template before printing')
    return
  }

  if (labelProducts.value.length === 0) {
    toast.error('No products selected for printing')
    return
  }

  if (totalLabels.value === 0) {
    toast.error('Please set quantities for products')
    return
  }

  try {
    if (printSettings.value.type === 'thermal') {
      await showThermalPrintPreview()
    } else {
      // Show browser print preview first, then generate content
      showPrintPreview.value = true
      // Wait for the modal to render, then generate content
      await nextTick()
      await generatePrintContent() // Now async
    }
  } catch (error) {
    console.error('Print error:', error)
    toast.error('Error preparing labels for printing')
  }
}

// Helper function to generate CSS for custom label sizes
const generateCustomLabelCSS = (mediaType) => {
  const customTemplates = stickerTemplates.value.filter(t => t.isCustom)
  return customTemplates.map(template => {
    const width = parseInt(template.width) || 25
    const height = parseInt(template.height) || 25
    
    if (mediaType === 'print') {
      return `.${template.printClass} { width: ${width}mm; height: ${height}mm; }`
    } else {
      // Screen size - roughly 3px per mm
      const screenWidth = width * 3
      const screenHeight = height * 3
      return `.${template.printClass} { width: ${screenWidth}px; height: ${screenHeight}px; }`
    }
  }).join('\n        ')
}

// Helper function to get field value for custom templates
const getCustomFieldValue = (field, product) => {
  const sampleData = {
    business_name: 'Sample Business',
    business_phone: '+91 98765 43210',
    business_email: 'info@sample.com',
    business_address: '123 Sample St',
    business_gstin: '22AAAAA0000A1Z5',
    product_name: product.name,
    product_barcode: product.barcode,
    product_price: `₹${product.pricing?.sale_price_inc_tax || 'N/A'}`,
    product_mrp: `₹${product.pricing?.mrp || 'N/A'}`,
    product_short_code: product.short_code,
    product_category: product.category,
    product_barcode_image: product.barcode || product.short_code, // Barcode data
    custom: field.content || field.label,
    horizontal_line: '───────────',
    vertical_line: '│'
  }
  
  return sampleData[field.type] || field.label
}

// Helper function to get field value for preview (using sample data)
const getPreviewFieldValue = (field) => {
  const previewData = {
    business_name: 'Sample Business',
    business_phone: '+91 98765 43210',
    business_email: 'info@sample.com',
    business_address: '123 Sample St',
    business_gstin: '22AAAAA0000A1Z5',
    product_name: 'Sample Product',
    product_barcode: '1234567890123',
    product_price: '₹299',
    product_mrp: '₹399',
    product_short_code: 'SP001',
    product_category: 'Electronics',
    product_barcode_image: '[BARCODE]',
    custom: field.content || field.label,
    horizontal_line: '────',
    vertical_line: '│'
  }
  
  return previewData[field.type] || field.label || field.content || 'Field'
}

// Generate barcode as base64 data URL
const generateBarcode = (data) => {
  try {
    if (!data) return ''
    
    // Create a canvas element for barcode generation
    const canvas = document.createElement('canvas')
    JsBarcode(canvas, data, {
      format: "CODE128",
      width: 2,
      height: 60,
      displayValue: false,
      margin: 0
    })
    
    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('Error generating barcode:', error)
    return ''
  }
}

// Convert pixel positions to mm for print
const convertPositionToMm = (pxValue) => {
  if (!pxValue) return '0mm'
  const px = parseInt(pxValue.replace('px', '')) || 0
  // More accurate conversion: assuming 96 DPI, 1mm = 3.78 pixels
  const mm = px / 3.78
  return `${Math.round(mm * 100) / 100}mm` // Round to 2 decimal places
}

const generatePrintContent = async () => {
  if (!selectedTemplate.value) {
    console.error('No template selected for printing')
    toast.error('Please select a template before printing')
    return
  }

  const printContent = document.getElementById('print-preview-content')
  if (!printContent) {
    console.error('Print preview content element not found')
    toast.error('Print preview not available. Please try again.')
    return
  }

  let html = `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
      
      @media print {
        body { margin: 0; padding: 0; font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .no-print { display: none !important; }
        
        /* All dimensions now handled by inline styles for accuracy */
        .label {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-size: 8pt;
          position: relative;
          background: white;
        }
          position: relative;
          background: white;
        }
        
        .label .name { font-weight: 600; font-size: 7pt; margin-bottom: 1mm; }
        .label .barcode { font-family: 'Courier New', monospace; font-size: 12pt; margin: 1mm 0; }
        .label .price { font-weight: 600; color: #000; margin-top: 1mm; }
        .label .code { font-size: 6pt; color: #666; }
        .label .field { font-size: 8pt; line-height: 1.1; font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .label .barcode-image { max-width: 20mm; height: auto; }
        .label .line { display: block; overflow: hidden; }
      }
      
      @media screen {
        /* All dimensions now handled by inline styles for accuracy */
        .label {
          border: 2px solid #374151;
          border-radius: 2px;
          padding: 4px;
          margin: 2px;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-size: 10px;
          display: inline-block;
          vertical-align: top;
          text-align: center;
          box-sizing: border-box;
          background: white;
          position: relative;
        }
        
        .label .name { font-weight: 600; font-size: 9px; margin-bottom: 2px; }
        .label .barcode { font-family: 'Courier New', monospace; font-size: 10px; margin: 2px 0; }
        .label .price { font-weight: 600; color: #000; margin-top: 2px; }
        .label .code { font-size: 8px; color: #666; }
        .label .field { font-size: 10px; line-height: 1.1; font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .label .barcode-image { max-width: 80px; height: auto; }
        .label .line { display: block; overflow: hidden; }
      }
    </style>
    
    <div style="display: flex; flex-wrap: wrap; gap: 2mm; justify-content: flex-start;">
  `

  // Generate labels for each product
  for (const product of labelProducts.value) {
    const quantity = product.labelQuantity || 0
    for (let i = 0; i < quantity; i++) {
      if (selectedTemplate.value.isCustom && selectedTemplate.value.fields) {
        // Get actual dimensions for inline styles
        const labelWidth = parseInt(selectedTemplate.value.width) || 50
        const labelHeight = parseInt(selectedTemplate.value.height) || 25
        
        // Custom template with positioned fields - exact match to template design with actual dimensions
        html += `<div class="label" style="
          position: relative; 
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          width: ${labelWidth}mm;
          height: ${labelHeight}mm;
          display: inline-block;
          vertical-align: top;
          border: 2px solid #374151;
          border-radius: 1mm;
          padding: 2mm;
          margin: 1mm;
          box-sizing: border-box;
          page-break-inside: avoid;
          background: white;
        ">`
        
        for (const field of selectedTemplate.value.fields) {
          const fieldValue = getCustomFieldValue(field, product)
          const leftMm = convertPositionToMm(field.x)
          const topMm = convertPositionToMm(field.y)
          
          // Apply field styling properties exactly as designed
          const fontSize = field.fontSize || '8pt'
          const fontWeight = field.fontWeight || 'normal'
          const textAlign = field.textAlign || 'left'
          const color = field.color || '#000000'
          const textDecoration = field.textDecoration || 'none'
          
          const fieldStyle = `position: absolute; left: ${leftMm}; top: ${topMm}; font-size: ${fontSize}; font-weight: ${fontWeight}; text-align: ${textAlign}; color: ${color}; text-decoration: ${textDecoration}; line-height: 1; font-family: 'Inter', system-ui, -apple-system, sans-serif; margin: 0; padding: 0;`
          
          if (field.type === 'product_barcode_image') {
            // Generate barcode for this field
            const barcodeData = product.barcode || product.short_code || product._id
            const barcodeDataURL = generateBarcode(barcodeData)
            if (barcodeDataURL) {
              html += `<img src="${barcodeDataURL}" class="barcode-image" style="${fieldStyle}" alt="Barcode" />`
            }
          } else if (field.type === 'horizontal_line') {
            // Render horizontal line
            html += `<div class="line field" style="${fieldStyle}; border-bottom: 1px solid ${color}; width: 20mm; height: 1px;">&nbsp;</div>`
          } else if (field.type === 'vertical_line') {
            // Render vertical line  
            html += `<div class="line field" style="${fieldStyle}; border-left: 1px solid ${color}; width: 1px; height: 10mm;">&nbsp;</div>`
          } else {
            // Regular text field
            html += `<div class="field" style="${fieldStyle}">${fieldValue}</div>`
          }
        }
        
        html += '</div>'
      } else {
        // Get actual dimensions for standard templates
        const labelWidth = parseInt(selectedTemplate.value.width) || 50
        const labelHeight = parseInt(selectedTemplate.value.height) || 25
        
        // Default template structure with actual dimensions
        html += `
          <div class="label" style="
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            width: ${labelWidth}mm;
            height: ${labelHeight}mm;
            display: inline-block;
            vertical-align: top;
            border: 2px solid #374151;
            border-radius: 1mm;
            padding: 2mm;
            margin: 1mm;
            box-sizing: border-box;
            page-break-inside: avoid;
            background: white;
            text-align: center;
          ">
            <div class="name" style="font-weight: 600;">${product.name}</div>
            ${product.barcode ? `
              <div class="barcode">${product.barcode}</div>
              <div class="code">${product.barcode}</div>
            ` : `
              <div class="code">${product.short_code}</div>
            `}
            ${selectedTemplate.value.showPrice ? `
              <div class="price" style="font-weight: 600;">₹${product.pricing?.sale_price_inc_tax || 'N/A'}</div>
            ` : ''}
          </div>
        `
      }
    }
  }

  html += '</div>'
  printContent.innerHTML = html
  
  console.log('Print content generated successfully with custom template design')
}

const actuallyPrint = () => {
  window.print()
  showPrintPreview.value = false
  toast.success('Print dialog opened')
}

// Thermal Preview Functions
const showThermalPrintPreview = async () => {
  if (!connectedPrinter.value) {
    toast.error('No thermal printer connected')
    return
  }

  if (!selectedTemplate.value) {
    toast.error('No template selected')
    return
  }

  if (labelProducts.value.length === 0) {
    toast.error('No products to print')
    return
  }

  try {
    showThermalPreview.value = true
    thermalPreviewLoading.value = true
    thermalPreviews.value = []

    const previews = []
    
    // Generate previews for each label
    for (const product of labelProducts.value) {
      const quantity = product.labelQuantity || 1
      
      for (let i = 0; i < quantity; i++) {
        if (printSettings.value.thermalMode === 'image') {
          // Generate canvas preview
          const canvas = await createLabelCanvas(product, selectedTemplate.value)
          previews.push({
            product,
            canvas,
            canvasEl: null
          })
        } else {
          // Generate text preview
          const textPreview = await generateTextPreview(product, selectedTemplate.value)
          previews.push({
            product,
            textPreview
          })
        }
      }
    }
    
    thermalPreviews.value = previews
    thermalPreviewLoading.value = false

    // For image mode, render canvases after DOM update
    if (printSettings.value.thermalMode === 'image') {
      await nextTick()
      renderPreviewCanvases()
    }

  } catch (error) {
    console.error('Error generating thermal preview:', error)
    toast.error('Failed to generate preview')
    thermalPreviewLoading.value = false
  }
}

const generateTextPreview = async (product, template) => {
  let preview = ''
  
  // Initialize printer output
  preview += '--- THERMAL PRINTER OUTPUT ---\n\n'
  
  if (template.fields && template.fields.length > 0) {
    // Sort fields by Y position for top-to-bottom rendering
    const sortedFields = [...template.fields].sort((a, b) => (parseInt(a.y) || 0) - (parseInt(b.y) || 0))
    
    for (const field of sortedFields) {
      let content = ''
      
      // Get field content (same logic as thermal printing)
      switch (field.type) {
        case 'product_name':
          content = product.name || ''
          break
        case 'product_price':
          content = `₹${product.pricing?.sale_price_inc_tax || '0.00'}`
          break
        case 'product_mrp':
          content = `MRP: ₹${product.pricing?.mrp || '0.00'}`
          break
        case 'product_barcode':
          content = product.barcode || ''
          break
        case 'product_short_code':
          content = product.short_code || ''
          break
        case 'product_category':
          content = product.category || ''
          break
        case 'product_barcode_image':
          content = `[BARCODE: ${product.barcode || 'N/A'}]`
          break
        case 'horizontal_line':
          content = '─'.repeat(25)
          break
        case 'vertical_line':
          content = '│'
          break
        case 'business_name':
          content = authStore.business?.name || 'Business Name'
          break
        case 'business_phone':
          content = authStore.business?.phone || ''
          break
        case 'business_email':
          content = authStore.business?.email || ''
          break
        case 'business_address':
          content = authStore.business?.address || ''
          break
        case 'business_gstin':
          content = authStore.business?.gstin || ''
          break
        default:
          content = field.text || ''
      }
      
      if (content) {
        // Add field with some basic formatting indication
        const fontWeight = field.fontWeight === 'bold' ? '[BOLD] ' : ''
        const fontSize = field.fontSize ? `[${field.fontSize}] ` : ''
        preview += `${fontWeight}${fontSize}${content}\n`
      }
    }
  } else {
    // Basic product info fallback
    preview += `[BOLD] ${product.name || 'Product'}\n`
    preview += `Price: ₹${product.pricing?.sale_price_inc_tax || '0.00'}\n`
    if (product.barcode) {
      preview += `[BARCODE: ${product.barcode}]\n`
      preview += `${product.barcode}\n`
    }
  }
  
  preview += '\n--- END LABEL ---'
  return preview
}

const renderPreviewCanvases = () => {
  thermalPreviews.value.forEach((preview, index) => {
    if (preview.canvas && preview.canvasEl) {
      // Copy canvas content to preview element
      const ctx = preview.canvasEl.getContext('2d')
      preview.canvasEl.width = preview.canvas.width
      preview.canvasEl.height = preview.canvas.height
      ctx.drawImage(preview.canvas, 0, 0)
    }
  })
}

const proceedWithThermalPrint = async () => {
  showThermalPreview.value = false
  await actualThermalPrint()
}

const actualThermalPrint = async () => {
  // This is the original printToThermalPrinter logic
  try {
    let allPrintData = []
    
    // Use different printing methods based on user preference
    if (printSettings.value.thermalMode === 'image') {
      // Image-based printing
      toast.info('Generating images for printing...', { duration: 3000 })
      
      for (const product of labelProducts.value) {
        const quantity = product.labelQuantity || 1
        
        for (let i = 0; i < quantity; i++) {
          // Create canvas and convert to bitmap
          const canvas = await createLabelCanvas(product, selectedTemplate.value)
          const bitmap = canvasToMonochromeBitmap(canvas)
          const imageCommands = generateImagePrintCommands(bitmap)
          allPrintData.push(imageCommands)
        }
      }
    } else {
      // Text-based printing (existing method)
      for (const product of labelProducts.value) {
        const quantity = product.labelQuantity || 1
        
        for (let i = 0; i < quantity; i++) {
          const thermalData = await generateThermalPrintData(product, selectedTemplate.value)
          allPrintData.push(thermalData)
        }
      }
    }
    
    // Combine and send data
    const combinedData = printSettings.value.thermalMode === 'image' 
      ? combineImageDataForPrinting(allPrintData)
      : combineDataForPrinting(allPrintData)

    // Send to printer
    await sendDataToThermalPrinter(combinedData)
    
    toast.success(`Successfully sent ${totalLabels.value} labels to thermal printer`)
  } catch (error) {
    console.error('Thermal printing error:', error)
    toast.error(`Printing failed: ${error.message}`)
  }
}

// Image-Based Thermal Printing Functions
const createLabelCanvas = async (product, template) => {
  return new Promise((resolve, reject) => {
    try {
      // Create canvas with actual label dimensions
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Convert mm to pixels at 203 DPI (8 pixels per mm)
      const pixelsPerMm = 8
      const widthMm = parseInt(template.width) || 50
      const heightMm = parseInt(template.height) || 25
      
      canvas.width = widthMm * pixelsPerMm
      canvas.height = heightMm * pixelsPerMm
      
      // Set canvas to white background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      // Set default text properties
      ctx.textBaseline = 'top'
      ctx.fillStyle = '#000000'
      
      // Process each field in the template
      if (template.fields && template.fields.length > 0) {
        const promises = template.fields.map(field => renderFieldToCanvas(ctx, field, product, pixelsPerMm))
        
        Promise.all(promises).then(() => {
          resolve(canvas)
        }).catch(reject)
      } else {
        // Fallback: render basic product info
        renderBasicProductToCanvas(ctx, product, canvas.width, canvas.height)
        resolve(canvas)
      }
    } catch (error) {
      reject(error)
    }
  })
}

const renderFieldToCanvas = async (ctx, field, product, pixelsPerMm) => {
  const x = (parseInt(field.x) || 0) * pixelsPerMm / 96 * 25.4 // Convert from CSS pixels to canvas pixels
  const y = (parseInt(field.y) || 0) * pixelsPerMm / 96 * 25.4
  
  let content = ''
  
  // Get field content
  switch (field.type) {
    case 'product_name':
      content = product.name || ''
      break
    case 'product_price':
      content = `₹${product.pricing?.sale_price_inc_tax || '0.00'}`
      break
    case 'product_mrp':
      content = `MRP: ₹${product.pricing?.mrp || '0.00'}`
      break
    case 'product_barcode':
      content = product.barcode || ''
      break
    case 'product_short_code':
      content = product.short_code || ''
      break
    case 'product_category':
      content = product.category || ''
      break
    case 'product_barcode_image':
      await renderBarcodeToCanvas(ctx, product.barcode, x, y, field)
      return
    case 'horizontal_line':
      renderLineToCanvas(ctx, x, y, field)
      return
    case 'vertical_line':
      content = '|'
      break
    case 'business_name':
      content = authStore.business?.name || 'Business Name'
      break
    case 'business_phone':
      content = authStore.business?.phone || ''
      break
    case 'business_email':
      content = authStore.business?.email || ''
      break
    case 'business_address':
      content = authStore.business?.address || ''
      break
    case 'business_gstin':
      content = authStore.business?.gstin || ''
      break
    default:
      content = field.text || ''
  }
  
  if (content) {
    // Set font properties with larger scaling for thermal printers
    let fontSize = parseInt(field.fontSize) || 12
    if (typeof field.fontSize === 'string') {
      fontSize = parseInt(field.fontSize.replace('px', '').replace('pt', ''))
    }
    
    const fontWeight = field.fontWeight || 'normal'
    const fontFamily = 'Arial, sans-serif'
    
    // Scale font size better for thermal printer visibility
    // Use a larger scaling factor for better readability
    const scaledFontSize = Math.max(8, fontSize * pixelsPerMm / 6) // Increased scaling from /12 to /6
    
    ctx.font = `${fontWeight} ${scaledFontSize}px ${fontFamily}`
    ctx.fillStyle = field.color || '#000000'
    ctx.textAlign = field.textAlign || 'left'
    
    // Draw text
    ctx.fillText(content, x, y)
  }
}

const renderBarcodeToCanvas = async (ctx, barcodeData, x, y, field) => {
  if (!barcodeData) return
  
  try {
    // Create a temporary canvas for barcode with enhanced settings
    const barcodeCanvas = document.createElement('canvas')
    JsBarcode(barcodeCanvas, barcodeData, {
      format: "CODE128",
      width: 3, // Increased width for better visibility
      height: 60, // Increased height
      displayValue: true, // Show barcode text
      fontSize: 12, // Larger font for barcode text
      margin: 2,
      textMargin: 5
    })
    
    // Calculate size - use field dimensions or default larger size
    const width = parseInt(field.width) || Math.max(120, barcodeCanvas.width)
    const height = parseInt(field.height) || Math.max(80, barcodeCanvas.height)
    
    // Draw barcode to main canvas
    ctx.drawImage(barcodeCanvas, x, y, width, height)
  } catch (error) {
    console.error('Barcode rendering error:', error)
    // Fallback: draw text representation
    ctx.font = '12px Arial'
    ctx.fillStyle = '#000000'
    ctx.fillText(`[${barcodeData}]`, x, y)
  }
}

const renderLineToCanvas = (ctx, x, y, field) => {
  const width = parseInt(field.width) || 100
  ctx.strokeStyle = field.color || '#000000'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(x, y)
  ctx.lineTo(x + width, y)
  ctx.stroke()
}

const renderBasicProductToCanvas = (ctx, product, canvasWidth, canvasHeight) => {
  let yPos = 15
  
  // Product name - Larger font for better visibility
  ctx.font = 'bold 24px Arial'
  ctx.fillStyle = '#000000'
  ctx.fillText(product.name || 'Product', 15, yPos)
  yPos += 35
  
  // Price - Larger font
  ctx.font = '18px Arial'
  ctx.fillText(`Price: ₹${product.pricing?.sale_price_inc_tax || '0.00'}`, 15, yPos)
  yPos += 30
  
  // Barcode if available - Larger size
  if (product.barcode) {
    try {
      const barcodeCanvas = document.createElement('canvas')
      JsBarcode(barcodeCanvas, product.barcode, {
        format: "CODE128",
        width: 2, // Increased width
        height: 40, // Increased height
        displayValue: true,
        fontSize: 14, // Larger font for barcode text
        margin: 5
      })
      ctx.drawImage(barcodeCanvas, 15, yPos)
    } catch (error) {
      console.error('Basic barcode rendering error:', error)
    }
  }
}

const canvasToMonochromeBitmap = (canvas) => {
  const ctx = canvas.getContext('2d')
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data
  
  // Convert to monochrome using Floyd-Steinberg dithering
  const width = canvas.width
  const height = canvas.height
  const bitmap = new Array(width * height)
  
  // Convert to grayscale first
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])
    const index = i / 4
    bitmap[index] = gray
  }
  
  // Apply Floyd-Steinberg dithering
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x
      const oldPixel = bitmap[index]
      const newPixel = oldPixel > 127 ? 255 : 0
      bitmap[index] = newPixel
      
      const error = oldPixel - newPixel
      
      // Distribute error to neighboring pixels
      if (x + 1 < width) bitmap[index + 1] += error * 7 / 16
      if (y + 1 < height) {
        if (x - 1 >= 0) bitmap[index + width - 1] += error * 3 / 16
        bitmap[index + width] += error * 5 / 16
        if (x + 1 < width) bitmap[index + width + 1] += error * 1 / 16
      }
    }
  }
  
  // Convert to packed bitmap (8 pixels per byte)
  const bytesPerRow = Math.ceil(width / 8)
  const packedBitmap = new Uint8Array(bytesPerRow * height)
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const pixelIndex = y * width + x
      const byteIndex = y * bytesPerRow + Math.floor(x / 8)
      const bitIndex = 7 - (x % 8)
      
      if (bitmap[pixelIndex] === 0) { // Black pixel
        packedBitmap[byteIndex] |= (1 << bitIndex)
      }
    }
  }
  
  return {
    width,
    height,
    data: packedBitmap
  }
}

const generateImagePrintCommands = (bitmap) => {
  const commands = []
  
  // Initialize printer
  commands.push([0x1B, 0x40]) // ESC @ - Initialize
  
  // Set raster bit image mode
  const width = bitmap.width
  const height = bitmap.height
  const bytesPerRow = Math.ceil(width / 8)
  
  // Use GS v 0 command for raster bit image
  // GS v 0 m xL xH yL yH d1...dk
  commands.push([
    0x1D, 0x76, 0x30, 0x00, // GS v 0 (normal mode)
    bytesPerRow & 0xFF, (bytesPerRow >> 8) & 0xFF, // xL xH (bytes per line)
    height & 0xFF, (height >> 8) & 0xFF // yL yH (number of lines)
  ])
  
  // Add bitmap data
  for (let i = 0; i < bitmap.data.length; i += 512) { // GOOJPRT PT-265 optimized chunks
    const chunk = Array.from(bitmap.data.slice(i, i + 512))
    commands.push(chunk)
  }
  
  // Add line feeds and cut
  commands.push([0x0A, 0x0A]) // Line feeds
  commands.push([0x1D, 0x56, 0x42, 0x00]) // Partial cut
  
  return commands
}

const printToThermalPrinter = async () => {
  if (!connectedPrinter.value) {
    toast.error('No thermal printer connected')
    return
  }

  if (!selectedTemplate.value) {
    toast.error('No template selected')
    return
  }

  if (labelProducts.value.length === 0) {
    toast.error('No products to print')
    return
  }

  try {
    let allPrintData = []
    
    // Use different printing methods based on user preference
    if (printSettings.value.thermalMode === 'image') {
      // Image-based printing
      toast.info('Generating images for printing...', { duration: 3000 })
      
      for (const product of labelProducts.value) {
        const quantity = product.labelQuantity || 1
        
        for (let i = 0; i < quantity; i++) {
          // Create canvas and convert to bitmap
          const canvas = await createLabelCanvas(product, selectedTemplate.value)
          const bitmap = canvasToMonochromeBitmap(canvas)
          const imageCommands = generateImagePrintCommands(bitmap)
          allPrintData.push(imageCommands)
        }
      }
    } else {
      // Text-based printing (existing method)
      for (const product of labelProducts.value) {
        const quantity = product.labelQuantity || 1
        
        for (let i = 0; i < quantity; i++) {
          const thermalData = await generateThermalPrintData(product, selectedTemplate.value)
          allPrintData.push(thermalData)
        }
      }
    }
    
    // Combine and send data
    const combinedData = printSettings.value.thermalMode === 'image' 
      ? combineImageDataForPrinting(allPrintData)
      : combineDataForPrinting(allPrintData)
    
    // Send to thermal printer
    await sendDataToThermalPrinter(combinedData)
    
    toast.success(`Successfully printed ${totalLabels.value} labels`)
    
  } catch (error) {
    console.error('Thermal printing error:', error)
    toast.error('Failed to print: ' + error.message)
  }
}

const combineImageDataForPrinting = (allPrintData) => {
  // Flatten all image commands into a single array
  const combined = []
  
  allPrintData.forEach((labelData, index) => {
    // Add each label's commands
    labelData.forEach(command => {
      combined.push(...command)
    })
    
    // Add spacing between labels (except last one)
    if (index < allPrintData.length - 1) {
      combined.push(0x0A, 0x0A) // Two line feeds between images
    }
  })
  
  // GOOJPRT PT-265 optimized feed and cut
  combined.push(0x1B, 0x64, 0x03) // Feed 3 lines (GOOJPRT optimized)
  combined.push(0x1D, 0x56, 0x00) // Partial cut (GOOJPRT compatible)
  
  return new Uint8Array(combined)
}

const loadAllProducts = async () => {
  try {
    const businessId = authStore.businessId
    if (!businessId) return

    const response = await productsApi.getProducts({ business_id: businessId, limit: 10000 })
    allProducts.value = response.data || response.products || []
  } catch (error) {
    console.error('Error loading products:', error)
    toast.error('Failed to load products. Please try again.')
  }
}

// ESC/POS Command Generation Functions
const generateThermalPrintData = async (product, template) => {
  const commands = []
  
  // GOOJPRT PT-265 paper dimensions (58mm paper, 384 dots width)
  const paperWidthMm = 58 // GOOJPRT PT-265: 58mm paper
  const marginsMm = printSettings.value.thermalMargins || 2 // Default 2mm margins
  const usableWidthMm = paperWidthMm - (marginsMm * 2)
  
  // GOOJPRT PT-265 specifications: 384 dots for 58mm paper (203 DPI)
  const dotsPerMm = 384 / 58 // Exact ratio for GOOJPRT PT-265
  const labelWidthDots = 384 // Fixed width for GOOJPRT PT-265
  const templateHeightMm = parseInt(template.height) || 25
  const labelHeightDots = Math.floor(templateHeightMm * dotsPerMm)
  
  console.log(`Thermal paper: ${paperWidthMm}mm, usable: ${usableWidthMm}mm (${labelWidthDots} dots)`)
  
  // GOOJPRT PT-265 optimized initialization sequence
  commands.push([0x1B, 0x40]) // ESC @ - Initialize printer
  commands.push([0x1D, 0x7C, 0x02]) // Set maximum heat density (GOOJPRT optimized)
  commands.push([0x1B, 0x33, 0x00]) // Set line spacing to 0 (GOOJPRT optimized)
  commands.push([0x1B, 0x4D, 0x01]) // ESC M 1 - Select Font B (optimal for 58mm)
  
  // Set standard mode (not page mode) for better compatibility
  commands.push([0x1B, 0x53]) // ESC S - Standard mode
  
  // Set line spacing for better fit on small labels
  if (templateHeightMm <= 25) {
    commands.push([0x1B, 0x33, 0x10]) // ESC 3 - Set line spacing to 16/180 inch (tighter)
  } else {
    commands.push([0x1B, 0x32]) // ESC 2 - Set line spacing to 1/6 inch (normal)
  }
  
  // Set character size to normal
  commands.push([0x1D, 0x21, 0x00]) // GS ! - Set character size to normal
  
  // Process each field in the template
  if (template.fields && template.fields.length > 0) {
    // Sort fields by Y position to print top to bottom
    const sortedFields = [...template.fields].sort((a, b) => (parseInt(a.y) || 0) - (parseInt(b.y) || 0))
    
    for (const field of sortedFields) {
      await addFieldToThermalData(commands, field, product, labelWidthDots, labelHeightDots, usableWidthMm, templateHeightMm)
    }
  } else {
    // Fallback: basic product info layout
    await addBasicProductInfo(commands, product)
  }
  
  // Add spacing after label based on template height
  const feedLines = templateHeightMm <= 25 ? 1 : 2
  for (let i = 0; i < feedLines; i++) {
    commands.push([0x0A])
  }
  
  return commands
}

const addFieldToThermalData = async (commands, field, product, labelWidthDots, labelHeightDots, usableWidthMm, templateHeightMm) => {
  // Set font attributes based on field styling and paper size
  setFontAttributes(commands, field, usableWidthMm, templateHeightMm)
  
  // Set text alignment based on field properties
  if (field.textAlign) {
    switch (field.textAlign) {
      case 'center':
        commands.push([0x1B, 0x61, 0x01]) // ESC a 1 - Center align
        break
      case 'right':
        commands.push([0x1B, 0x61, 0x02]) // ESC a 2 - Right align
        break
      default:
        commands.push([0x1B, 0x61, 0x00]) // ESC a 0 - Left align
    }
  }
  
  let fieldContent = ''
  
  switch (field.type) {
    case 'product_name':
      fieldContent = product.name || ''
      break
    case 'product_price':
      fieldContent = `₹${product.pricing?.sale_price_inc_tax || '0.00'}`
      break
    case 'product_mrp':
      fieldContent = `MRP: ₹${product.pricing?.mrp || '0.00'}`
      break
    case 'product_barcode':
      fieldContent = product.barcode || ''
      break
    case 'product_short_code':
      fieldContent = product.short_code || ''
      break
    case 'product_category':
      fieldContent = product.category || ''
      break
    case 'product_barcode_image':
      // Reset to center alignment for barcode
      commands.push([0x1B, 0x61, 0x01]) // Center align
      await addBarcodeImage(commands, product.barcode)
      // Reset alignment after barcode
      commands.push([0x1B, 0x61, 0x00]) // Left align
      commands.push([0x0A]) // LF
      return
    case 'horizontal_line':
      // Reset to left align for lines
      commands.push([0x1B, 0x61, 0x00]) // Left align
      addHorizontalLine(commands, 32) // Full width line for thermal printer
      commands.push([0x0A]) // LF
      return
    case 'vertical_line':
      fieldContent = '│' // Use better vertical line character
      break
    case 'business_name':
      fieldContent = authStore.business?.name || 'Business Name'
      break
    case 'business_phone':
      fieldContent = authStore.business?.phone || ''
      break
    case 'business_email':
      fieldContent = authStore.business?.email || ''
      break
    case 'business_address':
      fieldContent = authStore.business?.address || ''
      break
    case 'business_gstin':
      fieldContent = authStore.business?.gstin || ''
      break
    default:
      fieldContent = field.text || ''
  }
  
  // Add text content with proper formatting and width management
  if (fieldContent) {
    // GOOJPRT PT-265 character width: 32 characters for Font B on 58mm paper
    const maxCharsPerLine = 32 // GOOJPRT PT-265 Font B specification
    
    // Add indentation based on X position (approximate)
    const xPos = parseInt(field.x) || 0
    const indentSpaces = Math.max(0, Math.floor(xPos / 20)) // Rough conversion
    const availableChars = maxCharsPerLine - indentSpaces
    
    // Wrap long text to fit on thermal paper
    const wrapText = (text, maxLength) => {
      if (text.length <= maxLength) return [text]
      
      const words = text.split(' ')
      const lines = []
      let currentLine = ''
      
      for (const word of words) {
        if ((currentLine + word).length <= maxLength) {
          currentLine += (currentLine ? ' ' : '') + word
        } else {
          if (currentLine) {
            lines.push(currentLine)
            currentLine = word
          } else {
            // Word is longer than max length, truncate it
            lines.push(word.substring(0, maxLength - 3) + '...')
            currentLine = ''
          }
        }
      }
      
      if (currentLine) lines.push(currentLine)
      return lines
    }
    
    const textLines = wrapText(fieldContent, availableChars)
    const indent = ' '.repeat(indentSpaces)
    
    for (const line of textLines) {
      const formattedContent = indent + line
      const textBytes = new TextEncoder().encode(formattedContent)
      commands.push(Array.from(textBytes))
      commands.push([0x0A]) // LF (Line Feed)
    }
  }
  
  // Reset alignment to left for next field
  commands.push([0x1B, 0x61, 0x00]) // ESC a 0 - Left align
}

const setFontAttributes = (commands, field, usableWidthMm = 44, templateHeightMm = 25) => {
  // Reset text attributes first
  commands.push([0x1B, 0x21, 0x00]) // ESC ! - Reset print mode
  
  let mode = 0x00
  
  // Font size handling - map CSS font sizes to thermal printer sizes with paper size consideration
  let fontSize = field.fontSize || '12px'
  if (typeof fontSize === 'string') {
    fontSize = parseInt(fontSize.replace('px', '').replace('pt', ''))
  }
  
  // Scale font sizes based on paper width
  const scaleFactor = usableWidthMm < 40 ? 0.8 : 1.0 // Smaller fonts for narrow paper
  const adjustedFontSize = fontSize * scaleFactor
  
  // For very small labels, use smaller fonts
  if (templateHeightMm <= 20) {
    if (adjustedFontSize >= 20) {
      mode |= 0x20 // Double width only
    } else if (adjustedFontSize >= 16) {
      mode |= 0x10 // Double height only
    }
    // Normal size for smaller fonts on small labels
  } else if (templateHeightMm <= 30) {
    if (adjustedFontSize >= 24) {
      mode |= 0x30 // Double height + Double width
    } else if (adjustedFontSize >= 18) {
      mode |= 0x20 // Double width
    } else if (adjustedFontSize >= 14) {
      mode |= 0x10 // Double height
    }
  } else {
    // Standard scaling for larger labels
    if (adjustedFontSize >= 28) {
      mode |= 0x30 // Double height + Double width
    } else if (adjustedFontSize >= 20) {
      mode |= 0x20 // Double width
    } else if (adjustedFontSize >= 16) {
      mode |= 0x10 // Double height
    }
  }
  
  // Font weight
  if (field.fontWeight === 'bold' || 
      field.fontWeight === '600' || 
      field.fontWeight === '700' || 
      field.fontWeight === '800' || 
      field.fontWeight === '900') {
    mode |= 0x08 // Emphasized (bold)
  }
  
  // Underline
  if (field.textDecoration && field.textDecoration.includes('underline')) {
    mode |= 0x80 // Underline
  }
  
  // Apply the combined mode
  commands.push([0x1B, 0x21, mode])
  
  // Alternative bold command for better compatibility
  if (field.fontWeight === 'bold' || 
      field.fontWeight === '600' || 
      field.fontWeight === '700' || 
      field.fontWeight === '800' || 
      field.fontWeight === '900') {
    commands.push([0x1B, 0x45, 0x01]) // ESC E - Emphasized mode on
  }
}

const addBarcodeImage = async (commands, barcodeText) => {
  if (!barcodeText) return
  
  try {
    // Reset any previous formatting
    commands.push([0x1B, 0x21, 0x00]) // Reset text mode
    
    // Center align for barcode
    commands.push([0x1B, 0x61, 0x01]) // Center alignment
    
    // Set barcode parameters
    commands.push([0x1D, 0x77, 0x02]) // GS w - Set barcode width (2 dots - more compatible)
    commands.push([0x1D, 0x68, 0x50]) // GS h - Set barcode height (80 dots)
    commands.push([0x1D, 0x48, 0x02]) // GS H - Print HRI below barcode
    commands.push([0x1D, 0x66, 0x00]) // GS f - Set HRI font to Font A
    
    // Print CODE128 barcode using the format: GS k m n d1...dn
    // where m = 73 (CODE128), n = length, d1...dn = data
    commands.push([0x1D, 0x6B, 0x49]) // GS k 73 - CODE128 barcode
    
    // Add barcode data with proper validation
    const cleanBarcodeText = barcodeText.replace(/[^A-Za-z0-9\s]/g, '') // Clean invalid characters
    const barcodeBytes = new TextEncoder().encode(cleanBarcodeText)
    
    if (barcodeBytes.length > 0 && barcodeBytes.length < 256) {
      commands.push([barcodeBytes.length]) // Length byte
      commands.push(Array.from(barcodeBytes))
    } else {
      throw new Error('Invalid barcode data length')
    }
    
    commands.push([0x0A]) // Line feed after barcode
    
    // Reset alignment
    commands.push([0x1B, 0x61, 0x00]) // Left alignment
    
  } catch (error) {
    console.error('Error generating barcode:', error)
    
    // Safe fallback: just print the barcode text
    commands.push([0x1B, 0x61, 0x01]) // Center align
    const fallbackText = `${barcodeText}`
    const textBytes = new TextEncoder().encode(fallbackText)
    commands.push(Array.from(textBytes))
    commands.push([0x0A]) // Line feed
    commands.push([0x1B, 0x61, 0x00]) // Reset alignment
  }
}

const addHorizontalLine = (commands, length) => {
  const line = '-'.repeat(length)
  const lineBytes = new TextEncoder().encode(line)
  commands.push(Array.from(lineBytes))
}

const addBasicProductInfo = async (commands, product) => {
  // Basic layout for products without custom template
  
  // Product name (bold, larger)
  commands.push([0x1B, 0x21, 0x08]) // Bold
  const nameBytes = new TextEncoder().encode(product.name || 'Product')
  commands.push(Array.from(nameBytes))
  commands.push([0x0A]) // New line
  
  // Reset formatting
  commands.push([0x1B, 0x21, 0x00])
  
  // Price
  const priceText = `Price: ₹${product.pricing?.sale_price_inc_tax || '0.00'}`
  const priceBytes = new TextEncoder().encode(priceText)
  commands.push(Array.from(priceBytes))
  commands.push([0x0A])
  
  // Barcode if available
  if (product.barcode) {
    await addBarcodeImage(commands, product.barcode)
    commands.push([0x0A])
    
    // Barcode text
    const barcodeBytes = new TextEncoder().encode(product.barcode)
    commands.push(Array.from(barcodeBytes))
    commands.push([0x0A])
  }
}

const combineDataForPrinting = (allPrintData) => {
  // Flatten all commands into a single array
  const combined = []
  
  allPrintData.forEach((labelData, index) => {
    // Add each label's commands
    labelData.forEach(command => {
      combined.push(...command)
    })
    
    // Add separation between labels (except last one)
    if (index < allPrintData.length - 1) {
      // Add more spacing between labels for better separation
      combined.push(0x0A, 0x0A, 0x0A) // Three line feeds between labels
      
      // Optional: Add a separator line
      const separatorLine = '-'.repeat(25)
      const separatorBytes = new TextEncoder().encode(separatorLine)
      combined.push(...separatorBytes)
      combined.push(0x0A, 0x0A) // Extra spacing after separator
    }
  })
  
  // GOOJPRT PT-265 optimized feed and cut
  combined.push(0x1B, 0x64, 0x03) // Feed 3 lines (GOOJPRT optimized)
  combined.push(0x1D, 0x56, 0x00) // Partial cut (GOOJPRT compatible)
  
  return new Uint8Array(combined)
}

const sendDataToThermalPrinter = async (data) => {
  if (!connectedPrinter.value || !connectedPrinter.value.characteristic) {
    throw new Error('No printer connected or characteristic not available')
  }
  
  const characteristic = connectedPrinter.value.characteristic
  const maxChunkSize = 512 // GOOJPRT PT-265 optimized chunk size (from official SDK)
  
  try {
    // Check if printer is still connected before sending
    if (!connectedPrinter.value.server.connected) {
      throw new Error('Printer disconnected before sending data')
    }
    
    // GOOJPRT PT-265 optimized transmission
    let totalSent = 0
    for (let i = 0; i < data.length; i += maxChunkSize) {
      const chunk = data.slice(i, i + maxChunkSize)
      
      try {
        await characteristic.writeValue(chunk)
        totalSent += chunk.length
        
        // Update progress for large print jobs
        if (data.length > 2048) {
          const progress = Math.round((totalSent / data.length) * 100)
          console.log(`Sending to printer: ${progress}%`)
        }
        
        // GOOJPRT PT-265 optimized delay (5ms from official SDK)
        await new Promise(resolve => setTimeout(resolve, 5))
        
      } catch (chunkError) {
        console.error('Error sending chunk:', chunkError)
        
        // Try to recover from temporary errors
        if (chunkError.name === 'NetworkError' || chunkError.name === 'InvalidStateError') {
          // Wait a bit longer and retry once
          await new Promise(resolve => setTimeout(resolve, 200))
          try {
            await characteristic.writeValue(chunk)
            totalSent += chunk.length
          } catch (retryError) {
            throw new Error(`Failed to send data chunk after retry: ${retryError.message}`)
          }
        } else {
          throw chunkError
        }
      }
    }
    
    console.log(`Successfully sent ${totalSent} bytes to thermal printer`)
    
    // Wait for printer to process the data
    await new Promise(resolve => setTimeout(resolve, 100))
    
  } catch (error) {
    console.error('Error sending data to printer:', error)
    
    // Provide specific error messages
    if (error.name === 'NetworkError') {
      throw new Error('Printer communication failed. Please check connection.')
    } else if (error.name === 'InvalidStateError') {
      throw new Error('Printer is not ready. Please try again.')
    } else if (error.message.includes('disconnected')) {
      // Update UI state
      bluetoothStatus.value = 'disconnected'
      connectedPrinter.value = null
      throw new Error('Printer disconnected during printing.')
    } else {
      throw new Error('Failed to send data to printer: ' + error.message)
    }
  }
}

// Thermal Printer Functions
const scanForPrinters = async () => {
  if (!('bluetooth' in navigator)) {
    toast.error('Bluetooth not supported in this browser')
    return
  }

  isScanning.value = true
  bluetoothStatus.value = 'connecting'
  
  try {
    const device = await navigator.bluetooth.requestDevice({
      filters: [
        { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // GOOJPRT PT-265 service
        { namePrefix: 'MTP-' },
        { namePrefix: 'PTP-' },
        { namePrefix: 'RPP' },
        { namePrefix: 'GOOJPRT' }, // GOOJPRT specific
        { namePrefix: 'PT-265' }, // GOOJPRT PT-265 specific
        { namePrefix: 'BlueTooth Printer' },
        { namePrefix: 'Printer' }
      ],
      optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
    })

    if (device) {
      await connectToPrinter(device)
    }
  } catch (error) {
    console.error('Bluetooth scan error:', error)
    if (error.name !== 'NotFoundError') {
      toast.error('Failed to scan for printers: ' + error.message)
    }
    bluetoothStatus.value = 'disconnected'
  } finally {
    isScanning.value = false
  }
}

const connectToPrinter = async (device) => {
  bluetoothStatus.value = 'connecting'
  
  try {
    console.log('Connecting to printer:', device.name)
    
    // Set connection timeout
    const connectionTimeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Connection timeout')), 10000)
    )
    
    const connectPromise = async () => {
      const server = await device.gatt.connect()
      
      // Try common thermal printer service UUIDs
      let service, characteristic
      
      try {
        // Try standard thermal printer service
        service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb')
        characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb')
      } catch (serviceError) {
        console.log('Standard service not found, trying alternative services...')
        
        // Try alternative service UUIDs for different printer models
        const serviceUUIDs = [
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Common Bluetooth serial service
          '0000ff00-0000-1000-8000-00805f9b34fb',  // Alternative thermal service
          '6e400001-b5a3-f393-e0a9-e50e24dcca9e'   // Nordic UART service
        ]
        
        const characteristicUUIDs = [
          '49535343-1e4d-4bd9-ba61-23c647249616',
          '0000ff01-0000-1000-8000-00805f9b34fb',
          '6e400002-b5a3-f393-e0a9-e50e24dcca9e'
        ]
        
        let connected = false
        for (let i = 0; i < serviceUUIDs.length && !connected; i++) {
          try {
            service = await server.getPrimaryService(serviceUUIDs[i])
            characteristic = await service.getCharacteristic(characteristicUUIDs[i])
            connected = true
            console.log(`Connected using service UUID ${i + 1}`)
          } catch (altError) {
            console.log(`Service UUID ${i + 1} failed:`, altError.message)
          }
        }
        
        if (!connected) {
          throw new Error('No compatible printer service found. This device may not be a supported thermal printer.')
        }
      }
      
      return { server, service, characteristic }
    }
    
    // Race between connection and timeout
    const { server, service, characteristic } = await Promise.race([
      connectPromise(),
      connectionTimeout
    ])
    
    connectedPrinter.value = {
      device,
      server,
      service,
      characteristic,
      name: device.name || 'Thermal Printer'
    }
    
    bluetoothStatus.value = 'connected'
    toast.success(`Connected to ${connectedPrinter.value.name}`)
    
    // Handle disconnect events with enhanced error information
    device.addEventListener('gattserverdisconnected', (event) => {
      console.log('Printer disconnected:', event)
      
      const wasExpected = bluetoothStatus.value === 'disconnected'
      bluetoothStatus.value = 'disconnected'
      connectedPrinter.value = null
      
      if (!wasExpected) {
        toast.warning('Printer disconnected unexpectedly. Check printer power and distance.')
      }
    })
    
  } catch (error) {
    console.error('Connection error:', error)
    bluetoothStatus.value = 'disconnected'
    connectedPrinter.value = null
    
    // Provide specific error messages
    let errorMessage = 'Failed to connect to printer'
    
    if (error.message.includes('timeout')) {
      errorMessage = 'Connection timeout. Make sure the printer is on and nearby.'
    } else if (error.message.includes('GATT Server is disconnected')) {
      errorMessage = 'Printer is not responding. Try turning it off and on again.'
    } else if (error.message.includes('service found')) {
      errorMessage = error.message // Use our custom service error message
    } else if (error.name === 'NetworkError') {
      errorMessage = 'Bluetooth connection failed. Check if the printer is in pairing mode.'
    } else if (error.name === 'NotSupportedError') {
      errorMessage = 'This printer model is not supported.'
    } else if (error.message) {
      errorMessage += ': ' + error.message
    }
    
    toast.error(errorMessage)
  }
}

const disconnectCurrentPrinter = () => {
  if (connectedPrinter.value && connectedPrinter.value.server.connected) {
    connectedPrinter.value.server.disconnect()
  }
  bluetoothStatus.value = 'disconnected'
  connectedPrinter.value = null
  toast.info('Printer disconnected')
}

// Lifecycle
onMounted(() => {
  loadAllProducts()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
/* Component-specific styles */
.label-25x25 { width: 80px; height: 80px; }
.label-25x50 { width: 120px; height: 80px; }
.label-40x25 { width: 100px; height: 60px; }
.label-50x25 { width: 140px; height: 60px; }
.label-57x32 { width: 160px; height: 90px; }
.label-70x42 { width: 180px; height: 100px; }
</style>